/**
 * AdvancedFormModal - Full-Featured Form Modal Preset
 *
 * Advanced configuration with all features enabled:
 * - 4xl size (large forms)
 * - Character counter enabled
 * - Dirty check enabled
 * - Preview panel support
 * - Multi-step progress
 * - All features active
 *
 * Ideal for:
 * - Complex multi-step wizards
 * - Forms with preview panels
 * - Large data entry forms with many fields
 * - Forms requiring advanced features
 *
 * @module advanced-form-modal
 */

'use client';

import * as React from 'react';

import { EnhancedFormModal, type EnhancedFormModalProps } from '../enhanced-form-modal';

/**
 * AdvancedFormModal props (same as core, all features available)
 */
export type AdvancedFormModalProps = EnhancedFormModalProps;

/**
 * AdvancedFormModal - Advanced form modal preset
 *
 * Full-featured configuration for complex forms with preview panels,
 * multi-step wizards, and all advanced features.
 *
 * @example Basic usage with preview
 * ```tsx
 * <AdvancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Create Layout"
 *   subtitle="Design your custom dashboard layout"
 *   previewContent={<LayoutPreview layout={formData} />}
 *   previewPosition="right"
 *   previewWidth={400}
 *   enableDirtyCheck
 *   isDirty={isDirty}
 * >
 *   <LayoutForm />
 * </AdvancedFormModal>
 * ```
 *
 * @example Multi-step wizard with progress
 * ```tsx
 * <AdvancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Vehicle Registration Wizard"
 *   showProgress
 *   currentStep={currentStep}
 *   totalSteps={4}
 *   enableDirtyCheck
 *   isDirty={isDirty}
 *   isSubmitting={isSubmitting}
 * >
 *   <VehicleFormStep step={currentStep} />
 * </AdvancedFormModal>
 * ```
 *
 * @example With loading timeout
 * ```tsx
 * <AdvancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Import Data"
 *   isLoading={isLoadingData}
 *   loadingTimeout={5000}
 *   onLoadingTimeout={() => toast.error("Taking too long...")}
 * >
 *   <ImportForm />
 * </AdvancedFormModal>
 * ```
 */
export function AdvancedFormModal(props: AdvancedFormModalProps) {
  return (
    <EnhancedFormModal
      {...props}
      // Preset configuration with sensible defaults
      size={props.size ?? { base: 'full', lg: 'xl' }}
      showCharacterCount={props.showCharacterCount ?? true}
      enableKeyboardShortcuts={props.enableKeyboardShortcuts ?? true}
      autoFocusFirstField={props.autoFocusFirstField ?? true}
      // All other features controlled by explicit props
    />
  );
}

AdvancedFormModal.displayName = 'AdvancedFormModal';
